import { useState, useEffect, useRef } from 'react';

interface FullscreenExitButtonProps {
  onExit: () => void;
}

/**
 * Floating, auto-hiding "Exit fullscreen" button.
 * Essential on iOS where there is no Esc key and the Fullscreen API is unavailable,
 * so the CSS immersive fallback needs an on-screen way out.
 */
export function FullscreenExitButton({ onExit }: FullscreenExitButtonProps) {
  const [visible, setVisible] = useState(true);
  const timerRef = useRef<number>();

  useEffect(() => {
    const reveal = () => {
      setVisible(true);
      clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setVisible(false), 3500);
    };

    reveal(); // show on mount, then start the hide timer
    window.addEventListener('pointermove', reveal);
    window.addEventListener('touchstart', reveal);
    window.addEventListener('keydown', reveal);

    return () => {
      clearTimeout(timerRef.current);
      window.removeEventListener('pointermove', reveal);
      window.removeEventListener('touchstart', reveal);
      window.removeEventListener('keydown', reveal);
    };
  }, []);

  return (
    <button
      onClick={onExit}
      aria-label="Exit fullscreen"
      className={`fixed top-4 right-4 z-[70] flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full text-sm font-medium text-white transition-all duration-300 ${
        visible ? 'opacity-90 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
      }`}
      style={{
        background: 'rgba(10, 10, 18, 0.72)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
      </svg>
      Exit
    </button>
  );
}
